import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, ListGroup, Form, Pagination, Button, Spinner, Card, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import CandidatoViewModel from '../../../viewmodel/CandidatoViewModel';
import { useNavigate, useParams } from 'react-router-dom';
import { StatusCandidato } from '../../../model/Candidato/Candidato';
import { useBreadcrumbs } from '../../../Routes/BreadcrumbContext';
import { BsPlus, BsDash, BsCameraVideoFill, BsPersonStanding, BsPersonStandingDress, BsLock, BsPersonX, BsCheckCircle, BsPlayCircle, BsStopCircle, BsExclamationTriangleFill, BsFileText, BsInfoCircle, BsQuestionCircle } from 'react-icons/bs';
import { FaChild } from 'react-icons/fa';
import { BancaViewModel } from '../../../viewmodel/banca/BancaViewModel';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import { TipoBancaViewModel } from '../../../viewmodel/banca/TipoBancaViewModel';
import { TipoBanca, TipoBancaNome } from '../../../model/banca/Banca';
import { toast } from 'react-toastify';
import useUserStore from '../../../stores/userStore';
import { PermissaoEnum } from '../../../model/Usuario/Permissao';
import HelpDrawerVincular from './HelpDrawerVincular';
import './VincularCandidatosBanca.css';

const VincularCandidatosBanca: React.FC = () => {
    const { processoId, bancaId } = useParams<{ processoId: string, bancaId: string }>();
    const { setBreadcrumbs } = useBreadcrumbs();
    const navigate = useNavigate();

    const [tipoBanca, setTipoBanca] = useState<TipoBanca>();
    const [candidatosNaoVinculados, setCandidatosNaoVinculados] = useState<any[]>([]);
    const [candidatosVinculados, setCandidatosVinculados] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [estatisticas, setEstatisticas] = useState<any>({});

    // Filtros e paginação para candidatos não vinculados
    const [filtrosNaoVinculados, setFiltrosNaoVinculados] = useState({
        nome: '',
        nomeSocial: '',
        inscricao: '',
        status: '',
        hasRecurso: undefined as boolean | undefined,
        apenasEditaisEmComum: undefined as boolean | undefined,
        page: 0,
        size: 6
    });

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [candidatoParaVincular, setCandidatoParaVincular] = useState<{id: string, nome: string} | null>(null);
    const [bancaTemEditais, setBancaTemEditais] = useState<boolean>(false);
    const [showHelpDrawer, setShowHelpDrawer] = useState(false);

    // Filtros e paginação para candidatos vinculados
    const [filtrosVinculados, setFiltrosVinculados] = useState({
        nome: '',
        nomeSocial: '',
        inscricao: '',
        status: '',
        page: 0,
        size: 6
    });

    const [totalNaoVinculados, setTotalNaoVinculados] = useState(0);
    const [totalVinculados, setTotalVinculados] = useState(0);
    const [totalPagesNaoVinculados, setTotalPagesNaoVinculados] = useState(0);
    const [totalPagesVinculados, setTotalPagesVinculados] = useState(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    // Hook de debounce
    const useDebounce = (value: any, delay: number) => {
        const [debouncedValue, setDebouncedValue] = useState(value);

        useEffect(() => {
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);

            return () => {
                clearTimeout(handler);
            };
        }, [value, delay]);

        return debouncedValue;
    };

    // Valores com debounce
    const debouncedFiltrosNaoVinculados = useDebounce(filtrosNaoVinculados, 1500);
    const debouncedFiltrosVinculados = useDebounce(filtrosVinculados, 1500);

    useEffect(() => {
        const usuario = useUserStore.getState().usuario;
        const permissoesUsuario = usuario?.perfil?.permissoes?.map(p => p.nome) || [];
        const temPermissaoConsultarProcesso = permissoesUsuario.includes(PermissaoEnum.CONSULTAR_PROCESSO);
        
        const breadcrumbs = [
            { path: '/', breadcrumb: '🏠' },
            ...(temPermissaoConsultarProcesso ? [{ path: '/processos', breadcrumb: 'Processos' }] : []),
            { path: `/processos/${processoId}/bancas/${bancaId}/vincular-candidatos`, breadcrumb: 'Vincular Candidatos à Banca' },
        ];
        
        setBreadcrumbs(breadcrumbs);
    }, []);

    useEffect(() => {
        const fetchTipoBanca = async () => {
            try {
                const tipo = await TipoBancaViewModel.getTipoBanca(bancaId!);
                setTipoBanca(tipo);
            } catch (error) {
                console.log(BackendErrorMessage(error));
            }
        };

        fetchTipoBanca();
    }, [bancaId]);

    useEffect(() => {
        if (tipoBanca) {
            fetchCandidatos();
        }
    }, [debouncedFiltrosNaoVinculados, debouncedFiltrosVinculados, tipoBanca]);

    const resetAutoRefresh = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        intervalRef.current = setInterval(() => {
            fetchCandidatos(true);
        }, 30000);
    }, []);

    const fetchCandidatos = async (isAutoRefresh = false) => {
        if (!isAutoRefresh) {
            setIsLoading(true);
        }
        resetAutoRefresh();
        try {
            const params = {
                // Parâmetros para candidatos não vinculados
                nomeNaoVinculado: debouncedFiltrosNaoVinculados.nome || undefined,
                nomeSocialNaoVinculado: debouncedFiltrosNaoVinculados.nomeSocial || undefined,
                inscricaoNaoVinculado: debouncedFiltrosNaoVinculados.inscricao || undefined,
                statusNaoVinculado: debouncedFiltrosNaoVinculados.status || undefined,
                hasRecursoNaoVinculado: debouncedFiltrosNaoVinculados.hasRecurso,
                apenasEditaisEmComum: debouncedFiltrosNaoVinculados.apenasEditaisEmComum || undefined,
                pageNaoVinculado: debouncedFiltrosNaoVinculados.page,
                sizeNaoVinculado: debouncedFiltrosNaoVinculados.size,
                sortNaoVinculado: 'nome',

                // Parâmetros para candidatos vinculados
                nomeVinculado: debouncedFiltrosVinculados.nome || undefined,
                nomeSocialVinculado: debouncedFiltrosVinculados.nomeSocial || undefined,
                inscricaoVinculado: debouncedFiltrosVinculados.inscricao || undefined,
                statusVinculado: debouncedFiltrosVinculados.status || undefined,
                pageVinculado: debouncedFiltrosVinculados.page,
                sizeVinculado: debouncedFiltrosVinculados.size,
                sortVinculado: 'nome'
            };

            const response = await CandidatoViewModel.getCandidatosVinculacaoTecnico(
                processoId!,
                bancaId!,
                params
            );

            console.log('Response completa:', response);
            console.log('Candidatos não vinculados:', response.candidatosNaoVinculados.content);

            setCandidatosNaoVinculados(response.candidatosNaoVinculados.content);
            setCandidatosVinculados(response.candidatosVinculados.content);
            setEstatisticas(response.estatisticas);

            setTotalNaoVinculados(response.candidatosNaoVinculados.totalElements);
            setTotalVinculados(response.candidatosVinculados.totalElements);
            setTotalPagesNaoVinculados(response.candidatosNaoVinculados.totalPages);
            setTotalPagesVinculados(response.candidatosVinculados.totalPages);

            setBancaTemEditais(response.bancaTemEditais);

        } catch (error) {
            console.error('Erro ao buscar candidatos:', error);
            toast.error(BackendErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    };

    const handleVincularCandidato = async (candidatoId: string, candidatoNome: string, hasEditalEmComum: boolean) => {
        if (bancaTemEditais && !hasEditalEmComum) {
            setCandidatoParaVincular({ id: candidatoId, nome: candidatoNome });
            setShowConfirmModal(true);
            return;
        }
        await vincularCandidato(candidatoId);
    };

    const vincularCandidato = async (candidatoId: string) => {
        try {
            const sucesso = await CandidatoViewModel.vincularCandidato(processoId!, bancaId!, candidatoId);
            if (sucesso) {
                toast.success('Candidato vinculado com sucesso!');
                fetchCandidatos();
            }
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        }
    };

    const confirmarVinculacao = async () => {
        if (candidatoParaVincular) {
            await vincularCandidato(candidatoParaVincular.id);
        }
        setShowConfirmModal(false);
        setCandidatoParaVincular(null);
    };

    const handleDesvincularCandidato = async (candidatoId: string) => {
        try {
            await CandidatoViewModel.desvincularCandidato(processoId!, bancaId!, candidatoId);
            toast.success('Candidato desvinculado com sucesso!');
            fetchCandidatos();
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        }
    };

    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    const getStatusBackgroundClass = (status: string) => {
        switch (status) {
            case 'VALIDADO': return 'bg-success bg-opacity-10';
            case 'NAO_VALIDADO': return 'bg-danger bg-opacity-10';
            case 'NAO_AVALIADO': return 'bg-warning bg-opacity-10';
            case 'INCONCLUSIVO': return 'bg-warning bg-opacity-25';
            case 'AUSENTE': return 'bg-secondary bg-opacity-10';
            default: return '';
        }
    };



    const renderCandidatoNaoVinculado = (item: any) => {
        const { candidato, hasRecurso, hasEditalEmComum, hasResponsavel } = item;
        const nomeExibicao = candidato.nomeSocial || candidato.nome;
        const statusBgClass = getStatusBackgroundClass(candidato.status);
        const isMenor = hasResponsavel === true || candidato.responsavel != null;
        
        console.log('Debug candidato:', { 
            nome: nomeExibicao, 
            hasResponsavel,
            hasResponsavelType: typeof hasResponsavel,
            responsavel: candidato.responsavel,
            isMenor 
        });

        return (
            <ListGroup.Item key={candidato.id} className={`d-flex justify-content-between align-items-center ${statusBgClass} ${hasRecurso ? 'border-warning border-2' : ''}`}>
                <div className="d-flex align-items-center">
                    <div className="me-3 position-relative">
                        {hasRecurso && <BsCameraVideoFill size={14} className="text-dark position-absolute" style={{ top: '-5px', right: '-5px' }} />}
                        {candidato.status === StatusCandidato.AUSENTE ? (
                            <BsPersonX size={20} className="text-danger" />
                        ) : candidato.nomeSocial ? (
                            <BsPersonStandingDress size={20} className="text-primary" />
                        ) : (
                            <BsPersonStanding size={20} className="text-primary" />
                        )}
                    </div>
                    <div>
                        <div>
                            <strong>{nomeExibicao}</strong>
                            {hasRecurso && (
                                <OverlayTrigger overlay={<Tooltip>Candidato com recurso</Tooltip>}>
                                    <span style={{ display: 'inline-block' }}>
                                        <BsCheckCircle size={14} className="text-success ms-2" />
                                    </span>
                                </OverlayTrigger>
                            )}
                            {bancaTemEditais && (
                                hasEditalEmComum ? (
                                    <OverlayTrigger overlay={<Tooltip>Candidato com edital em comum com a banca</Tooltip>}>
                                        <span style={{ display: 'inline-block' }}>
                                            <BsFileText size={14} className="text-success ms-2" />
                                        </span>
                                    </OverlayTrigger>
                                ) : (
                                    <OverlayTrigger overlay={<Tooltip>Candidato SEM edital em comum com a banca</Tooltip>}>
                                        <span style={{ display: 'inline-block' }}>
                                            <BsExclamationTriangleFill size={14} className="text-danger ms-2" />
                                        </span>
                                    </OverlayTrigger>
                                )
                            )}
                            {isMenor && (
                                <OverlayTrigger overlay={<Tooltip>Candidato menor de idade (18-)</Tooltip>}>
                                    <span style={{ display: 'inline-block' }}>
                                        <FaChild size={14} className="text-info ms-2" />
                                    </span>
                                </OverlayTrigger>
                            )}
                        </div>
                        {candidato.nomeSocial && candidato.nome !== candidato.nomeSocial && (
                            <div className="text-muted small">Nome: {candidato.nome}</div>
                        )}
                        <div className="text-muted small">
                            {candidato.inscricao} | {candidato.status}
                        </div>
                    </div>
                </div>
                <OverlayTrigger overlay={<Tooltip>Vincular à banca</Tooltip>}>
                    <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleVincularCandidato(candidato.id, nomeExibicao, hasEditalEmComum)}
                        disabled={isLoading}
                    >
                        <BsPlus size={16} />
                    </Button>
                </OverlayTrigger>
            </ListGroup.Item>
        );
    };

    const getStatusVotacaoIcon = (statusVotacao: string, candidatoStatus?: string) => {
        // Se o candidato está ausente, exibe ícone específico para ausente
        if (candidatoStatus === StatusCandidato.AUSENTE) {
            return (
                <OverlayTrigger overlay={<Tooltip>Candidato ausente</Tooltip>}>
                    <span style={{ display: 'inline-block' }}>
                        <BsPersonX size={16} className="text-danger ms-2" />
                    </span>
                </OverlayTrigger>
            );
        }

        switch (statusVotacao) {
            case 'NAO_INICIADA':
                return (
                    <OverlayTrigger overlay={<Tooltip>Heteroidentificação não iniciada</Tooltip>}>
                        <span style={{ display: 'inline-block' }}>
                            <BsStopCircle size={16} className="text-secondary ms-2" />
                        </span>
                    </OverlayTrigger>
                );
            case 'ABERTA':
                return (
                    <OverlayTrigger overlay={<Tooltip>Heteroidentificação aberta</Tooltip>}>
                        <span style={{ display: 'inline-block' }}>
                            <BsPlayCircle size={16} className="text-info ms-2" />
                        </span>
                    </OverlayTrigger>
                );
            case 'FECHADA':
                return (
                    <OverlayTrigger overlay={<Tooltip>Heteroidentificação fechada</Tooltip>}>
                        <span style={{ display: 'inline-block' }}>
                            <BsCheckCircle size={16} className="text-success ms-2" />
                        </span>
                    </OverlayTrigger>
                );
            default: return null;
        }
    };

    const renderCandidatoVinculado = (item: any) => {
        const { candidato, hasResponsavel } = item;
        const nomeExibicao = candidato.nomeSocial || candidato.nome;
        const isAusente = candidato.status === StatusCandidato.AUSENTE;
        const isMenor = hasResponsavel === true || candidato.responsavel != null;
        const borderClass = isAusente ? '' : (item.hasRecurso ? 'border-warning border-1' :
            (item.statusVotacao === 'NAO_INICIADA' ? 'border-info border-1' :
                (item.statusVotacao === 'ABERTA' ? 'border-success border-1' : '')));
        const bgClass = isAusente ? '' : (item.hasRecurso ? 'bg-warning bg-opacity-10' :
            (item.statusVotacao === 'NAO_INICIADA' ? 'bg-info bg-opacity-10' :
                (item.statusVotacao === 'ABERTA' ? 'bg-success bg-opacity-10' : '')));

        return (
            <ListGroup.Item key={item.id} className={`d-flex justify-content-between align-items-center ${borderClass} ${bgClass}`}>
                <div className="d-flex align-items-center">
                    <div className="me-3 position-relative">
                        {item.hasRecurso && <BsCameraVideoFill size={14} className="text-warning position-absolute" style={{ top: '-5px', right: '-5px' }} />}
                        {candidato.status === StatusCandidato.AUSENTE ? (
                            <BsPersonX size={20} className="text-danger" />
                        ) : candidato.nomeSocial ? (
                            <BsPersonStandingDress size={20} className="text-primary" />
                        ) : (
                            <BsPersonStanding size={20} className="text-primary" />
                        )}
                    </div>
                    <div className="flex-grow-1">
                        <div>
                            <strong>{nomeExibicao}</strong>
                            {item.hasRecurso && (
                                <OverlayTrigger overlay={<Tooltip>Candidato com recurso</Tooltip>}>
                                    <span style={{ display: 'inline-block' }}>
                                        <BsExclamationTriangleFill size={14} className="text-warning ms-2" />
                                    </span>
                                </OverlayTrigger>
                            )}
                            {getStatusVotacaoIcon(item.statusVotacao, candidato.status)}
                            {isMenor && (
                                <OverlayTrigger overlay={<Tooltip>Candidato menor de idade (18-)</Tooltip>}>
                                    <span style={{ display: 'inline-block' }}>
                                        <FaChild size={14} className="text-info ms-2" />
                                    </span>
                                </OverlayTrigger>
                            )}
                        </div>
                        {candidato.nomeSocial && candidato.nome !== candidato.nomeSocial && (
                            <div className="text-muted small">Nome: {candidato.nome}</div>
                        )}
                        <div className="text-muted small">
                            {candidato.inscricao}
                        </div>
                    </div>
                </div>
                {item.statusVotacao === 'FECHADA' ? (
                    <OverlayTrigger overlay={<Tooltip>Desvinculação bloqueada - Heteroidentificação fechada</Tooltip>}>
                        <Button variant="secondary" size="sm" disabled>
                            <BsLock size={16} />
                        </Button>
                    </OverlayTrigger>
                ) : (
                    <OverlayTrigger overlay={<Tooltip>Desvincular da banca</Tooltip>}>
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDesvincularCandidato(candidato.id)}
                            disabled={isLoading}
                        >
                            <BsDash size={16} />
                        </Button>
                    </OverlayTrigger>
                )}
            </ListGroup.Item>
        );
    };

    const renderPaginacao = (
        currentPage: number,
        totalPages: number,
        onPageChange: (page: number) => void
    ) => {
        if (totalPages <= 1) return null;

        const items = [];
        for (let page = 0; page < totalPages; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => onPageChange(page)}
                >
                    {page + 1}
                </Pagination.Item>
            );
        }

        return (
            <Pagination className="justify-content-center">
                <Pagination.Prev
                    disabled={currentPage === 0}
                    onClick={() => onPageChange(currentPage - 1)}
                />
                {items}
                <Pagination.Next
                    disabled={currentPage === totalPages - 1}
                    onClick={() => onPageChange(currentPage + 1)}
                />
            </Pagination>
        );
    };

    return (
        <HelpDrawerVincular isOpen={showHelpDrawer} onClose={() => setShowHelpDrawer(false)}>
        <Container fluid>
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <h2>Vincular Candidatos à Banca</h2>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setShowHelpDrawer(true)}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
                        >
                            <BsQuestionCircle /> Ajuda
                        </button>
                    </div>
                </Col>
            </Row>
            <Row>
                {/* Candidatos Não Vinculados */}
                <Col md={6}>
                    <Card>
                        <Card.Header>
                            <h5>Candidatos Não Vinculados ({totalNaoVinculados})</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-2">
                                <Col md={4}>
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        placeholder="Nome..."
                                        value={filtrosNaoVinculados.nome}
                                        onChange={(e) => setFiltrosNaoVinculados(prev => ({ ...prev, nome: e.target.value, page: 0 }))}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        placeholder="Nome Social..."
                                        value={filtrosNaoVinculados.nomeSocial}
                                        onChange={(e) => setFiltrosNaoVinculados(prev => ({ ...prev, nomeSocial: e.target.value, page: 0 }))}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        placeholder="Inscrição..."
                                        value={filtrosNaoVinculados.inscricao}
                                        onChange={(e) => setFiltrosNaoVinculados(prev => ({ ...prev, inscricao: e.target.value, page: 0 }))}
                                    />
                                </Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={6}>
                                    <Form.Select
                                        size="sm"
                                        value={filtrosNaoVinculados.status}
                                        onChange={(e) => setFiltrosNaoVinculados(prev => ({ ...prev, status: e.target.value, page: 0 }))}
                                    >
                                        <option value="">Status</option>
                                        <option value="NAO_AVALIADO">NAO_AVALIADO</option>
                                        <option value="INCONCLUSIVO">INCONCLUSIVO</option>
                                        <option value="AUSENTE">AUSENTE</option>
                                        <option value="NAO_VALIDADO">NÃO VALIDADO</option>
                                    </Form.Select>
                                </Col>
                                <Col md={6}>
                                    <Form.Select
                                        size="sm"
                                        value={filtrosNaoVinculados.hasRecurso === undefined ? '' : filtrosNaoVinculados.hasRecurso.toString()}
                                        onChange={(e) => setFiltrosNaoVinculados(prev => ({ ...prev, hasRecurso: e.target.value === '' ? undefined : e.target.value === 'true', page: 0 }))}
                                    >
                                        <option value="">Recurso</option>
                                        <option value="true">Com recurso</option>
                                        <option value="false">Sem recurso</option>
                                    </Form.Select>
                                </Col>
                            </Row>
                            {bancaTemEditais && (
                                <Row className="mb-3">
                                    <Col>
                                        <Form.Check
                                            type="checkbox"
                                            label="Apenas candidatos com editais esperados para a banca"
                                            checked={filtrosNaoVinculados.apenasEditaisEmComum}
                                            onChange={(e) => setFiltrosNaoVinculados(prev => ({ ...prev, apenasEditaisEmComum: e.target.checked, page: 0 }))}
                                        />
                                    </Col>
                                </Row>
                            )}

                            {isLoading ? (
                                <div className="text-center">
                                    <Spinner animation="border" />
                                </div>
                            ) : candidatosNaoVinculados.length === 0 ? (
                                <div className="text-center py-5">
                                    <BsInfoCircle size={48} className="text-muted mb-3" />
                                    <p className="text-muted">
                                        Não há candidatos com status Não Validado, Inconclusivo ou Ausente no Processo.
                                    </p>
                                    {tipoBanca?.nome === TipoBancaNome.RECURSAL && (
                                        <p className="text-muted small mb-3">
                                            Caso existam candidatos Não Validados,{' '}
                                            <Button 
                                                variant="link" 
                                                size="sm"
                                                className="p-0 text-decoration-underline"
                                                onClick={() => navigate(`/tecnico/bancas`)}
                                            >
                                                verifique se os vídeos foram enviados
                                            </Button>.
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <ListGroup style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        {candidatosNaoVinculados.map(renderCandidatoNaoVinculado)}
                                    </ListGroup>
                                    {renderPaginacao(
                                        filtrosNaoVinculados.page,
                                        totalPagesNaoVinculados,
                                        (page) => setFiltrosNaoVinculados(prev => ({ ...prev, page }))
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Candidatos Vinculados */}
                <Col md={6}>
                    <Card>
                        <Card.Header>
                            <h5>Candidatos Vinculados ({totalVinculados})</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-2">
                                <Col md={4}>
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        placeholder="Nome..."
                                        value={filtrosVinculados.nome}
                                        onChange={(e) => setFiltrosVinculados(prev => ({ ...prev, nome: e.target.value, page: 0 }))}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        placeholder="Nome Social..."
                                        value={filtrosVinculados.nomeSocial}
                                        onChange={(e) => setFiltrosVinculados(prev => ({ ...prev, nomeSocial: e.target.value, page: 0 }))}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        placeholder="Inscrição..."
                                        value={filtrosVinculados.inscricao}
                                        onChange={(e) => setFiltrosVinculados(prev => ({ ...prev, inscricao: e.target.value, page: 0 }))}
                                    />
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Select
                                        size="sm"
                                        value={filtrosVinculados.status}
                                        onChange={(e) => setFiltrosVinculados(prev => ({ ...prev, status: e.target.value, page: 0 }))}
                                    >
                                        <option value="">Status</option>
                                        <option value="VALIDADO">VALIDADO</option>
                                        <option value="NAO_VALIDADO">NÃO VALIDADO</option>
                                        <option value="NAO_AVALIADO">NAO_AVALIADO</option>
                                    </Form.Select>
                                </Col>
                            </Row>

                            {isLoading ? (
                                <div className="text-center">
                                    <Spinner animation="border" />
                                </div>
                            ) : (
                                <>
                                    <ListGroup style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        {candidatosVinculados.map(renderCandidatoVinculado)}
                                    </ListGroup>
                                    {renderPaginacao(
                                        filtrosVinculados.page,
                                        totalPagesVinculados,
                                        (page) => setFiltrosVinculados(prev => ({ ...prev, page }))
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            {estatisticas && (
                <Row className="mt-4">
                    <Col>
                        {/*
                        <Alert variant="info">
                             
                            {estatisticas.totalCandidatos} para vinculação | {` `}
                            {estatisticas.totalCandidatosBanca} vinculados | {` `}
                            
                            {estatisticas.candidatosComRecurso ? estatisticas.candidatosComRecurso + " candidato(s) com recurso" : ""}
                        </Alert>
                        */}
                    </Col>
                </Row>
            )}

            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Vinculação</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    O candidato <strong>{candidatoParaVincular?.nome}</strong> não possui editais em comum com esta banca.
                    Esta vinculação só deve ocorrer em casos excepcionais. Deseja continuar?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="warning" onClick={confirmarVinculacao}>
                        Confirmar Vinculação
                    </Button>
                </Modal.Footer>
            </Modal>

        </Container>
        </HelpDrawerVincular>
    );
};

export default VincularCandidatosBanca;